import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Download, History, Search, Trophy } from 'lucide-react';
import { lmsAPI } from '../services/api';
import { InlineLoader } from '../components/loading';
import { useAuthStore } from '../stores/authStore';
import { useTableSort, SortTh } from '../components/dashboard/useTableSort';
import { downloadTableXlsx, fileSlug, type TableCell } from '../utils/tableXlsx';

/**
 * Grow → Leaderboard.
 *
 * The web view of the leaderboard employees see in the mobile app's Grow tab:
 * the same points, the same order and the same two periods. Unlike the phone,
 * it lists everyone who has points (not the top 100) and says who each person
 * is and how the points were earned.
 */

type Period = 'monthly' | 'all_time';

interface LeaderRow {
  rank: number;
  employee_code: string;
  employee_name: string;
  role: string;
  store: string;
  city: string;
  manager: string;
  points: number;
  module_points: number;
  session_points: number;
  milestone_points: number;
  other_points: number;
}

const PERIODS: Array<{ key: Period; label: string; icon: typeof CalendarDays }> = [
  { key: 'monthly', label: 'This Month', icon: CalendarDays },
  { key: 'all_time', label: 'All Time', icon: History },
];

// The mobile screen's medal colours for ranks 1–3.
const MEDALS: Record<number, { bg: string; color: string; label: string }> = {
  1: { bg: '#FEF3C7', color: '#D97706', label: 'First place' },
  2: { bg: '#F3F4F6', color: '#6B7280', label: 'Second place' },
  3: { bg: '#FED7AA', color: '#EA580C', label: 'Third place' },
};

const RankBadge = ({ rank }: { rank: number }) => {
  const medal = MEDALS[rank];
  return (
    <span
      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold tabular-nums"
      style={medal ? { backgroundColor: medal.bg, color: medal.color } : { backgroundColor: '#F9FAFB', color: '#6B7280' }}
      title={medal ? `${medal.label} (rank ${rank})` : `Rank ${rank}`}
    >
      {medal ? <Trophy className="h-4 w-4" aria-label={`Rank ${rank}`} /> : rank}
    </span>
  );
};

const dash = (v: string) => v || '—';

export default function LmsLeaderboardPage() {
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  const projectId = selectedProjectId ?? undefined;

  const [period, setPeriod] = useState<Period>('monthly');
  const [rows, setRows] = useState<LeaderRow[]>([]);
  const [partial, setPartial] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    lmsAPI
      .getLeaderboard({ projectId, period })
      .then((resp) => {
        if (cancelled) return;
        const data = resp.data?.data;
        setRows(data?.rows ?? []);
        setPartial(data?.partial === true);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Could not load the leaderboard. Please try again.');
        setRows([]);
        setPartial(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectId, period]);

  // Search narrows the list but never renumbers it: a rank is the person's place
  // among everyone, which is what the phone shows them.
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      r.employee_name.toLowerCase().includes(needle) || r.employee_code.toLowerCase().includes(needle));
  }, [rows, search]);

  // Points nobody on mobile can earn today (older quiz bonuses, manual awards).
  // The column appears only when someone actually holds some.
  const showOther = useMemo(() => rows.some((r) => r.other_points !== 0), [rows]);

  const sort = useTableSort<LeaderRow>(filtered, {
    rank: (r) => r.rank,
    employee: (r) => r.employee_name,
    code: (r) => r.employee_code,
    role: (r) => r.role || null,
    store: (r) => r.store || null,
    city: (r) => r.city || null,
    manager: (r) => r.manager || null,
    modules: (r) => r.module_points,
    sessions: (r) => r.session_points,
    milestones: (r) => r.milestone_points,
    other: (r) => r.other_points,
    points: (r) => r.points,
  });

  const periodLabel = PERIODS.find((p) => p.key === period)?.label ?? '';

  // The table as shown: same rows, same order, same columns.
  const handleDownload = () => {
    const headers = [
      'Rank', 'Employee', 'Employee Code', 'Role', 'Store', 'City', 'Manager',
      'Modules', 'Sessions', 'Milestones', ...(showOther ? ['Other'] : []), 'Points',
    ];
    const body: TableCell[][] = sort.sorted.map((r) => [
      r.rank, r.employee_name, r.employee_code, r.role, r.store, r.city, r.manager,
      r.module_points, r.session_points, r.milestone_points, ...(showOther ? [r.other_points] : []), r.points,
    ]);
    void downloadTableXlsx(
      `leaderboard-${fileSlug(periodLabel)}-${new Date().toISOString().slice(0, 10)}`,
      { name: `Leaderboard ${periodLabel}`, headers, rows: body },
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Trophy className="h-7 w-7 text-primary-600" />
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Leaderboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Learning points ranking — the same leaderboard employees see in the mobile app's Grow tab.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
          {PERIODS.map((p) => {
            const Icon = p.icon;
            const isActive = p.key === period;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => setPeriod(p.key)}
                className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
                  isActive ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Icon className="h-4 w-4" />
                {p.label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name or employee code"
              aria-label="Search by name or employee code"
              className="w-64 rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            type="button"
            onClick={handleDownload}
            disabled={sort.sorted.length === 0 || loading}
            className="flex shrink-0 items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Download Excel
          </button>
        </div>
      </div>

      <section className="space-y-4 rounded-lg border border-gray-100 bg-white p-6">
        {partial && !loading && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
            Ranks and points are complete, but some employee details (name, role, store or manager) could not be
            loaded just now. Refresh in a minute to see them.
          </p>
        )}

        {!projectId ? (
          <p className="py-16 text-center text-sm text-gray-500">Select a project to see its leaderboard.</p>
        ) : loading ? (
          <InlineLoader label="Loading leaderboard…" className="flex justify-center py-16" />
        ) : error ? (
          <p className="py-16 text-center text-sm text-red-600">{error}</p>
        ) : rows.length === 0 ? (
          <p className="py-16 text-center text-sm text-gray-500">
            {period === 'monthly'
              ? 'Nobody has earned points this month yet.'
              : 'Nobody has earned points yet. Points come from completing modules and journeys and attending sessions.'}
          </p>
        ) : filtered.length === 0 ? (
          <p className="py-16 text-center text-sm text-gray-500">No one on the leaderboard matches "{search.trim()}".</p>
        ) : (
          <>
            <p className="text-xs text-gray-500">
              {filtered.length === rows.length
                ? `${rows.length} ${rows.length === 1 ? 'person' : 'people'} ranked`
                : `Showing ${filtered.length} of ${rows.length} people ranked`}
            </p>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="whitespace-nowrap text-left text-xs font-medium text-gray-500">
                    <SortTh label="Rank" sortKey="rank" sort={sort} align="center" className="px-4 py-3" />
                    <SortTh label="Employee" sortKey="employee" sort={sort} className="px-4 py-3" />
                    <SortTh label="Employee Code" sortKey="code" sort={sort} className="px-4 py-3" />
                    <SortTh label="Role" sortKey="role" sort={sort} className="px-4 py-3" />
                    <SortTh label="Store" sortKey="store" sort={sort} className="px-4 py-3" />
                    <SortTh label="City" sortKey="city" sort={sort} className="px-4 py-3" />
                    <SortTh label="Manager" sortKey="manager" sort={sort} className="px-4 py-3" />
                    <SortTh label="Modules" sortKey="modules" sort={sort} align="right" className="px-4 py-3" title="Points from completing modules" />
                    <SortTh label="Sessions" sortKey="sessions" sort={sort} align="right" className="px-4 py-3" title="Points from attending training sessions" />
                    <SortTh label="Milestones" sortKey="milestones" sort={sort} align="right" className="px-4 py-3" title="Bonus points for reaching a journey milestone" />
                    {showOther && (
                      <SortTh label="Other" sortKey="other" sort={sort} align="right" className="px-4 py-3" title="Points from any other source" />
                    )}
                    <SortTh label="Points" sortKey="points" sort={sort} align="right" className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sort.sorted.map((r) => (
                    <tr key={r.employee_code} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-center"><RankBadge rank={r.rank} /></td>
                      <td className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">{r.employee_name}</td>
                      <td className="whitespace-nowrap px-4 py-2 tabular-nums text-gray-600">{r.employee_code}</td>
                      <td className="px-4 py-2 text-gray-600">{dash(r.role)}</td>
                      <td className="px-4 py-2 text-gray-600">{dash(r.store)}</td>
                      <td className="px-4 py-2 text-gray-600">{dash(r.city)}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-600">{dash(r.manager)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-600">{r.module_points}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-600">{r.session_points}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-600">{r.milestone_points}</td>
                      {showOther && (
                        <td className="px-4 py-2 text-right tabular-nums text-gray-600">{r.other_points}</td>
                      )}
                      <td className="px-4 py-2 text-right text-base font-semibold tabular-nums text-gray-900">{r.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
